import React from 'react'
import { useFormikContext } from 'formik'
import { v4 as uuid4 } from 'uuid'
import clsx from 'clsx'
import { useStyles as commonStyles } from '../../FlyOutStorefront.styles'
import { useStyles } from './ListingDesignFormCards.styles'
import {
  ButtonV2,
  DragDropComponent,
  LabraToolTipV2,
  TextInputV2,
} from '@labrav/react-components'
import { ListingCardInCreation } from '../../../../modules/storefront/listingCardCreation/reducer'
import { Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronDown,
  faChevronUp,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons'
import { fetchProductDetailsFromUrl } from '../../../../modules/storefront/listingCardCreation/action'
import { useDispatch } from 'react-redux'
import { AnyAction } from '@reduxjs/toolkit'
import { get, isEmpty } from 'lodash'
export const ListingDesignFormCards: React.FC = () => {
  const { classes } = useStyles()
  const { classes: commonClasses } = commonStyles()
  const { values, setFieldValue, handleBlur, errors, touched } =
    useFormikContext<ListingCardInCreation>()
  const { listingCardData } = values
  const { product } = listingCardData
  const { listingCardData: listingCardDataErrors } = errors
  const { listingCardData: listingCardDataTouched } = touched
  const dispatch = useDispatch()
  // Open or close the card
  const handleChevronClick = (uniqueId: string) => () => {
    const updatedCards = product.map(card => {
      if (card.uniqueId === uniqueId) {
        card.isOpen = !card.isOpen
      }
      return card
    })
    setFieldValue('listingCardData.product', updatedCards)
  }
  // Delete a card
  const handleDelete = (uniqueId: string) => () => {
    const newCards = product.filter(card => card.uniqueId !== uniqueId)
    setFieldValue('listingCardData.product', newCards)
  }
  // Update the product link
  const updateProductLink =
    (uniqueId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const url = event.target?.value
      const updatedCards = product.map(card => {
        if (card.uniqueId === uniqueId) {
          card.url = url
        }
        return card
      })
      setFieldValue('listingCardData.product', [...updatedCards])
    }
  // Fetch details of the product
  const handleFetchDetails = (uniqueId: string, index: number) => async () => {
    await setFieldValue(`listingCardData.product[${index}].isLoading`, true)
    const url =
      (product.find(card => card.uniqueId === uniqueId)?.url as string) || ''
    if (!isEmpty(url)) {
      const match = url.match(/prodview-[a-zA-Z0-9]+/)
      const productId = match ? match[0] : ''
      await dispatch(
        fetchProductDetailsFromUrl(
          productId,
          index,
          values
        ) as unknown as AnyAction
      )
    }
    await setFieldValue(`listingCardData.product[${index}].isLoading`, false)
  }

  // Reorder the cards
  const handleReorder = (
    reorderedItems: { id: string; content: React.ReactNode }[]
  ) => {
    const newCards = reorderedItems.map((item, index) => {
      const card = product.find(card => card.uniqueId === item.id)
      return {
        ...card,
      }
    })
    setFieldValue('listingCardData.product', newCards)
  }
  // Add a card
  const addCard = () => {
    const newCards = [
      ...product,
      { url: '', uniqueId: String(uuid4()), isOpen: true },
    ]
    setFieldValue('listingCardData.product', newCards)
  }
  // Create items for drag and drop
  const items = product.map((card: any, index: number) => ({
    id: card.uniqueId,
    content: (
      <ListingProductCard
        isOpen={card.isOpen}
        id={card.uniqueId}
        index={index}
        url={card.url}
        onChevronClick={handleChevronClick(card.uniqueId)}
        error={get(listingCardDataErrors?.product, `${index}.url`, '')}
        touched={get(listingCardDataTouched?.product, `${index}.url`, false)}
        onDelete={handleDelete(card.uniqueId)}
        updateProductLink={updateProductLink(card.uniqueId)}
        handleBlur={handleBlur}
        handleFetchDetails={handleFetchDetails}
        shouldShowDelete={product.length !== 1}
        buttonLoading={get(
          listingCardData.product,
          `${index}.isLoading`,
          false
        )}
      />
    ),
  }))
  return (
    <div>
      <div className={classes.headingWrapper}>
        <Typography
          // className={clsx(commonClasses.sectionHeader, commonClasses.heading)}
          data-testid="product-cards-heading"
          className={classes.title}
        >
          Cards
        </Typography>
        <Typography className={commonClasses.description}>
          Drag cards to change the sequence.
        </Typography>
      </div>
      <div className={classes.dragContainer}>
        <DragDropComponent
          items={items}
          onDragEnd={handleReorder}
          gapBetween={3}
          secondaryVariant={true}
        />
      </div>
      <ButtonV2
        styleType="link"
        onClick={addCard}
        data-testid="add-a-card-button"
        disabled={product.length === 6}
      >
        Add card
      </ButtonV2>
    </div>
  )
}
export interface ListingProductCardProps {
  id: string
  index: number
  onChevronClick: () => void
  onDelete: () => void
  updateProductLink: (event: React.ChangeEvent<HTMLInputElement>) => void
  url: string
  handleBlur: (event: React.FocusEvent<HTMLInputElement>) => void
  handleFetchDetails: (url: string, index: number) => () => void
  isOpen: boolean
  error: string
  touched: boolean
  shouldShowDelete?: boolean
  buttonLoading: boolean
  shouldShowCheveron?: boolean
  shouldShowCardHeading?: boolean
  formikKey?: string
  shouldShowTitle?: boolean
  showHorizontalDivider?: boolean
}
export const ListingProductCard: React.FC<ListingProductCardProps> = ({
  id,
  index,
  url,
  isOpen,
  error,
  touched,
  shouldShowDelete,
  onChevronClick,
  onDelete,
  updateProductLink,
  handleBlur,
  handleFetchDetails,
  buttonLoading,
  shouldShowCheveron = true,
  shouldShowCardHeading = true,
  formikKey = 'listingCardData',
  shouldShowTitle = true,
  showHorizontalDivider = true,
}) => {
  const { classes } = useStyles()
  const { classes: commonClasses } = commonStyles()
  return (
    <div className={classes.card} data-testid="product-listing-card">
      <div className={classes.cardHeader}>
        {shouldShowCardHeading && (
          <div className={classes.headingContainer}>
            <Typography
              // className={clsx(
              //   commonClasses.sectionHeader,
              //   commonClasses.heading
              // )}
              className={classes.title}
            >{`Card ${index + 1}`}</Typography>
          </div>
        )}
        <div className={classes.iconsContainer}>
          {shouldShowDelete ? (
            <div className={classes.icon}>
              <FontAwesomeIcon
                icon={faTrash}
                onClick={onDelete}
                data-testid="product-card-delete"
              />
            </div>
          ) : null}
          {shouldShowCheveron && (
            <div className={classes.icon}>
              <FontAwesomeIcon
                icon={isOpen ? faChevronUp : faChevronDown}
                onClick={onChevronClick}
                data-testid="product-chevron-click"
              />
            </div>
          )}
        </div>
      </div>
      {isOpen ? (
        <div className={classes.cardBody}>
          {shouldShowTitle && (
            <Typography
              // className={commonClasses.sectionHeader}
              className={classes.titlecards}
            >
              Product link
            </Typography>
          )}
          <div className={classes.inputButtonContainer}>
            <div
              className={classes.productInputBlock}
              data-testid="product-input"
            >
              <TextInputV2
                value={url}
                updatedValue={url}
                keyName={`${formikKey}.product[${index}].url`}
                title="Enter AWS product link"
                type="text"
                placeHolder=""
                isError={false}
                onChange={updateProductLink}
                isReadOnly={false}
                isRequired={false}
                errorText={(!!touched && error) || ''}
                showSpinner={false}
                onBlur={handleBlur}
              />
            </div>
            {isEmpty(url) || !!error ? (
              <LabraToolTipV2
                label={'Please enter a valid product link.'}
                tooltipPlacmentGap="10px"
                children={
                  <ButtonV2
                    data-testid="product-card-fetch-details"
                    disabled={isEmpty(url) || !!error}
                    loading={buttonLoading}
                  >
                    Fetch details
                  </ButtonV2>
                }
                tooltipPlacement={'bottom'}
              />
            ) : (
              <ButtonV2
                onClick={handleFetchDetails(id, index)}
                data-testid="product-card-fetch-details"
                disabled={isEmpty(url) || !!error}
                loading={buttonLoading}
              >
                Fetch details
              </ButtonV2>
            )}
          </div>
          {/* <ButtonV2  styleType="link">Customize buttons for this card</ButtonV2> */}
        </div>
      ) : null}
      {showHorizontalDivider && (
        <div className={classes.horizontalDivider}></div>
      )}
    </div>
  )
}
